import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ProductListRow, ProductType } from "types/ProductCatalogue";
import { fetch as fetchProducts } from "./products.service";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

/** What the sidebar's chips choose between. */
export type TypeFilter = "all" | ProductType;

/**
 * The sidebar's list: pages of products matching the typed text and the chosen
 * type, appended as the list is scrolled. The server does both filtering and
 * paging. The service cancels the request it superseded, so only the newest
 * question is ever answered here.
 */
export function useProductList(companyId: string) {
  const [rows, setRows] = useState<ProductListRow[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  useEffect(() => {
    const handle = setTimeout(() => setSearchText(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const load = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (append) setIsLoadingMore(true);
      else setIsLoading(true);
      setIsError(false);
      try {
        const filtersValue: { textSearch?: string; type?: ProductType } = {};
        if (searchText) filtersValue.textSearch = searchText;
        if (typeFilter !== "all") filtersValue.type = typeFilter;
        const data = await fetchProducts(companyId, filtersValue, { page: pageToLoad, pageSize: PAGE_SIZE });
        const incoming: ProductListRow[] = data.productsCollection || [];
        const pagination = data.pagination || {};
        setRows((previous) => {
          if (!append) return incoming;
          // The catalogue may have shifted between two pages.
          const known = new Set(previous.map((row) => row.id));
          return [...previous, ...incoming.filter((row) => !known.has(row.id))];
        });
        setHasNextPage(Boolean(pagination.hasNextPage));
        setPage(pagination.page || pageToLoad);
        setIsLoading(false);
        setIsLoadingMore(false);
      } catch (error) {
        if (axios.isCancel(error)) return; // a newer request has taken over
        setIsError(true);
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [companyId, searchText, typeFilter]
  );

  // First page again whenever the company, the search or the type changes.
  useEffect(() => {
    setRows([]);
    setHasNextPage(false);
    load(1, false);
  }, [load]);

  // After a failure nothing is asked for until "Retry" is pressed, or a list that cannot load would be retried in a loop.
  const loadMore = () => {
    if (hasNextPage && !isLoading && !isLoadingMore && !isError) load(page + 1, true);
  };
  // Keeps the rows on screen while the first page is fetched again (after a product was saved).
  const reload = () => load(1, false);

  return {
    rows,
    isLoading,
    isLoadingMore,
    isError,
    hasNextPage,
    searchInput,
    setSearchInput,
    typeFilter,
    setTypeFilter,
    loadMore,
    reload,
  };
}

export type ProductList = ReturnType<typeof useProductList>;
